import * as React from 'react'
import { tw } from 'utils/classnames'

interface ResizeHandleProps {
  currentWidth: number
  minWidth: number
  maxWidth: number
  isDragging: boolean
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
  onReset: () => void
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void
  ariaLabel: string
  className?: string
  /** `line` is the gap bar used by Studio columns. `edge` is a wash on the panel's own edge. */
  variant?: 'line' | 'edge'
}

/**
 * @figma https://www.figma.com/design/q1XYGf6jz7Pnt6d5CQWL5E/Text-to-speech?node-id=2401-51519
 */
export function ResizeHandle({
  currentWidth,
  minWidth,
  maxWidth,
  isDragging,
  onPointerDown,
  onReset,
  onKeyDown,
  ariaLabel,
  className,
  variant = 'line',
}: ResizeHandleProps) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={currentWidth}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      tabIndex={0}
      data-dragging={isDragging ? '' : undefined}
      onPointerDown={onPointerDown}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      className={tw(
        'cursor-col-resize touch-none select-none outline-none',
        variant === 'line' &&
          'group/handle relative z-[1] -mx-1.5 flex h-full w-3 shrink-0 items-center justify-center px-[4.5px] focus-visible:outline-none',
        variant === 'edge' &&
          'absolute inset-y-0 left-0 w-2 bg-transparent hover:bg-theme_semantic_surface_border_color-subtle focus-visible:bg-theme_semantic_surface_border_color-strong',
        className,
      )}
    >
      {variant === 'line' ? (
        <span
          aria-hidden
          className={tw(
            'h-full w-[3px] bg-transparent transition-colors duration-100',
            'group-hover/handle:bg-theme_component_resizer_line-color-hover',
            'group-focus-visible/handle:bg-theme_component_resizer_line-color-hover',
            'group-data-[dragging]/handle:!bg-theme_component_resizer_line-color-active',
          )}
        />
      ) : null}
    </div>
  )
}
